import * as React from 'react';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';

import { navigate } from 'libs/router';

import ActionButtonDropdown from 'components/buttons/action-button-dropdown/ActionButtonDropdown';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';

import { __t, applyTranslations } from 'utils/language';

import './campaign-button-dropdown.scss';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('./campaign-button-dropdown.i18n.json'));

type OwnProps = {
  brandProfileId: number,
  withOffsiteCampaign?: boolean,
  withCreatorCampaign?: boolean,
  disabled?: boolean
};

type StateProps = {
  isCcmCreatorlessEnabled: boolean
};

type Props = OwnProps & StateProps;

const COMMON_MSG_KEY = 'collabary_common';
const MSG_KEY = 'campaign_button_dropdown';

const CampaignButtonDropdown: React.FC<Props> = ({
  brandProfileId,
  isCcmCreatorlessEnabled,
  withOffsiteCampaign,
  withCreatorCampaign,
  disabled = false
}): JSX.Element => {
  const handleClick = (
    event: React.SyntheticEvent<HTMLDivElement>
  ): void => {
    const target = event.currentTarget;
    const key = target.dataset.key ?? null;

    if (!isCcmCreatorlessEnabled) {
      const ccm = key === 'creator_campaign' ? '/ccm/create?campaign_type=creatorfull' : '/create';

      navigate.to(`/brand/profiles/${brandProfileId}/campaigns${ccm}`);
    } else {
      const ccm = key === 'creator_campaign' ? '/ccm/select-campaign-type' : '/create';

      navigate.to(`/brand/profiles/${brandProfileId}/campaigns${ccm}`);
    }
  };

  const renderEmpty = (): React.ReactNode => (
    <div className="campaign-button-dropdown__menu">
      <div className="campaign-button-dropdown__menu-item campaign-button-dropdown__menu-item--empty">
        {__t(`${MSG_KEY}.empty`)}
      </div>
    </div>
  );

  const renderLabel = (): React.ReactNode => (
    <div className="campaign-button-dropdown__label">
      <SVGDynamicIcon
        name="plus-custom-fg"
        width={13}
        height={13}
        customColor="white"
        className="campaign-button-dropdown__label-icon"
      />
      <div className="campaign-button-dropdown__label-text">
        {__t(`${MSG_KEY}.label`)}
      </div>
    </div>
  );

  const renderPill = (): React.ReactNode => (
    <div className="campaign-button-dropdown__menu-item-pill">
      {__t(`${COMMON_MSG_KEY}.basics.new`)}
    </div>
  );

  const renderZalandoLogo = (): React.ReactNode => (
    <img
      src="/static/images/zalando-logo.svg"
      className="campaign-button-dropdown__zalando-logo"
      alt={__t(`${MSG_KEY}.zalando_logo_alt`)}
    />
  );

  const renderMenuItem = (
    key: string
  ): React.ReactNode => (
    (
      <div
        key={`campaign-button-dropdown__menu-item--${key}`}
        className="campaign-button-dropdown__menu-item"
        data-key={key}
        onMouseDown={handleClick}
      >
        <div className="campaign-button-dropdown__menu-item-title">
          {__t(`${MSG_KEY}.items.${key}.title`)}
          {
            key === 'creator_campaign' && (
              renderPill()
            )
          }
        </div>
        <div className="campaign-button-dropdown__menu-item-contents">
          {__t(`${MSG_KEY}.items.${key}.description`)}
          {
            key === 'creator_campaign' && (
              renderZalandoLogo()
            )
          }
        </div>
      </div>
    )
  );

  const renderMenu = (): React.ReactNode => {
    const items = [] as Array<string>;

    if (withOffsiteCampaign) {
      items.push('offsite_campaign');
    }

    if (withCreatorCampaign) {
      items.push('creator_campaign');
    }

    if (isEmpty(items)) {
      return renderEmpty();
    }

    return (
      <div className="campaign-button-dropdown__menu">
        {
          items.map(renderMenuItem)
        }
      </div>
    );
  };

  return (
    <div className="campaign-button-dropdown">
      <ActionButtonDropdown
        name="campaign-button-dropdown__dropdown"
        label={renderLabel()}
        items={[]}
        width="normal"
        color="primary"
        disabled={disabled}
        withRightAlignedMenu
        withCustomWidthMenu
        CustomMenu={renderMenu()}
      />
    </div>
  );
};

const mapStateToProps = ({ runtimeConfig }): StateProps => ({
  isCcmCreatorlessEnabled: Boolean(runtimeConfig.config.isCcmEnabled)
    && Boolean(runtimeConfig.config.isCcmCreatorlessEnabled)
});

export default connect(mapStateToProps)(CampaignButtonDropdown);
